import { FormControl, InputLabel } from '@mui/material';
import MUIChip, { ChipProps } from '@mui/material/Chip';
import MUIMenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import MUISelect, { SelectProps } from '@mui/material/Select';
import { nanoid } from 'nanoid';
import React, { MouseEventHandler, useContext, useRef } from 'react';
import { ThemeContext, useTheme } from 'styled-components';
import { Icon } from '../../src/icon/index';
import { ClickToDismissPortalContext, ContainerContext } from '../../src/popover/context';
import { TextField } from '../../src/text-field/index';
interface IMenuItemProps extends Pick<MenuItemProps, 'selected' | 'sx' | 'disableGutters' | 'dense' | 'title'> {
	children?: React.ReactNode;
	value?: string;
	style?: React.CSSProperties;
	onClick?: MouseEventHandler<any>;
	disabled?: boolean;
}
const MenuItem = React.forwardRef<HTMLLIElement, IMenuItemProps>((props, ref) => {
	return <MUIMenuItem {...props} ref={ref} {...props} />;
});

export interface ISelectProps
	extends Pick<
		SelectProps,
		'startAdornment' | 'endAdornment' | 'maxRows' | 'open' | 'size' | 'variant' | 'inputComponent' | 'className' | 'required' | 'IconComponent'
	> {
	children?: React.ReactNode;
	label?: React.ReactNode;
	placeholder?: string;
	value?: string | string[];
	style?: React.CSSProperties;
	disabled?: boolean;
	labelId?: string;
	minWidth?: number;
	variant?: 'standard' | 'outlined' | 'filled';
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onInputChange?: (newVal: string) => void;
	inputValue?: string;
	disableUnderline?: boolean;
	multiple?: boolean;
	displayEmpty?: boolean;
	renderValue?: (value?: string | string[]) => React.ReactNode;
	readOnly?: boolean;
	search?: {
		onSearch: (search: string) => void;
		endAdorement?: React.ReactNode;
	};
	onOpen?: () => void;
	onClose?: () => void;
}

/**
 * This Select is extra depricated, it is only here to provide for @fox/data-grid. When we a better replacement for chip-select & the old select we should get rid of this one.
 * @deprecated
 */
export const DepcrecatedSelect = (props: ISelectProps) => {
	const popoverContext = React.useContext(ClickToDismissPortalContext);
	const popoverId = React.useRef(nanoid());

	const searchRef = React.useRef<HTMLInputElement>(null);

	const menuListElement = React.useRef<HTMLUListElement | null>();

	const [search, setSearch] = React.useState('');
	const openedChildIds = React.useRef<Record<string, boolean | (() => boolean)>>({});

	const anyChildOpen = Object.keys(openedChildIds.current).length > 0;

	const [internalVis, setInternalVis] = React.useState(props.open !== undefined ? props.open : false);
	const rootRef = React.useRef<HTMLElement>();
	const val = !Boolean(props.value) ? (props.multiple ? [] : '') : props.value;
	const container = useContext(ContainerContext);

	// Extract disableUnderline to prevent it from being spread to MUISelect
	const { disableUnderline, ...selectProps } = props;

	return (
		<>
			<ClickToDismissPortalContext.Provider
				value={{
					childPortalOpened: (id, dontBlockClose) => {
						openedChildIds.current[id] = dontBlockClose;
						if (popoverContext) popoverContext.childPortalOpened(id);
					},
					childPortalClosed: id => {
						popoverContext?.childPortalClosed(id);

						setTimeout(() => {
							delete openedChildIds.current[id];
						}, 100);
					}
				}}
			>
				<MUISelect
					disabled={props.disabled}
					open={props.open !== undefined ? props.open : internalVis}
					onOpen={() => {
						popoverContext.childPortalOpened(popoverId.current);
						setTimeout(() => {
							searchRef.current?.focus();
						}, 100);

						if (props.onOpen) {
							props.onOpen();
						}
						//if (props.open === undefined) {
						setInternalVis(true);
						//}
					}}
					onClose={() => {
						if (Object.keys(openedChildIds.current).length === 0) {
							popoverContext.childPortalClosed(popoverId.current);
							if (props.onClose) {
								props.onClose();
							}
							//if (props.open === undefined) {
							setInternalVis(false);
							//}
						}
					}}
					ref={rootRef}
					MenuProps={{
						container: container ?? undefined,
						style: {
							// something about how we're using MUI Select makes it not respect its maximum width, so do this to clamp that
							// nonesnese down
							maxWidth: Math.max(rootRef.current?.getBoundingClientRect().width, props.minWidth ?? 200)
						},
						onKeyDown: anyChildOpen
							? undefined
							: e => {
									if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter' && !anyChildOpen) {
										searchRef.current?.focus();
									}
								},
						MenuListProps: {
							ref: c => {
								menuListElement.current = c;
							}
						},
						PaperProps: {
							style: {
								maxHeight: 500
							},
							sx: {
								maxHeight: 500
							}
						}
					}}
					IconComponent={props.readOnly ? () => <></> : undefined}
					{...selectProps}
					value={val}
					size={props.size ?? 'small'}
				>
					{props.search && (
						<div
							style={{
								paddingLeft: 10,
								paddingRight: 10
							}}
							onKeyPress={
								anyChildOpen
									? undefined
									: e => {
											e.stopPropagation();
										}
							}
							onKeyUp={
								anyChildOpen
									? undefined
									: e => {
											e.stopPropagation();
										}
							}
						>
							<TextField
								//endAdorement={props.search.endAdorement}
								size={props.size ?? 'small'}
								value={props.inputValue ?? search}
								onChange={c => {
									setSearch(c.target.value);
									props.search?.onSearch?.(c.target.value);
									props.onInputChange?.(c.target.value);
								}}
								onKeyPress={
									anyChildOpen
										? undefined
										: e => {
												e.stopPropagation();
											}
								}
								onKeyDown={
									anyChildOpen
										? undefined
										: e => {
												if (e.key === 'ArrowDown') {
													menuListElement.current?.focus();
												} else {
													e.stopPropagation();
												}
											}
								}
								onKeyUp={
									anyChildOpen
										? undefined
										: e => {
												e.stopPropagation();
												e.preventDefault();
											}
								}
								inputRef={searchRef}
								autoFocus={true}
								placeholder={props.placeholder ?? 'Search'}
								style={{
									width: '100%'
								}}
							/>
						</div>
					)}
					{props.children}
				</MUISelect>
			</ClickToDismissPortalContext.Provider>
		</>
	);
};

interface IChipProps extends Pick<ChipProps, 'size' | 'variant' | 'onClick' | 'color' | 'onMouseDown' | 'onBlur' | 'key'> {
	label?: React.ReactNode;
	style?: React.CSSProperties;
	avatar?: React.ReactElement;
	onDelete?: React.EventHandler<any>;
}

/**
 * This Chip is deprecated, use `{ Chip } from '@fox/ui'`. Please see Storybook for help with Chip and report any issues.
 * @deprecated
 */
const Chip = (props: IChipProps) => {
	const theme = React.useContext(ThemeContext);
	return (
		<MUIChip
			size={props.size ?? 'small'}
			{...props}
			deleteIcon={
				<div
					style={{ display: 'flex', width: 28, height: '100%', opacity: 0.5 }}
					onMouseDown={e => {
						e.preventDefault();
						e.stopPropagation();
					}}
				>
					<Icon iconName="Clear" sx={{ color: theme?.colors.font, margin: 'auto', width: 17, height: 17 }} />
				</div>
			}
		/>
	);
};

/** we need this to simulate the placeholder effect */
function PlaceholderTextColorWrapper(props) {
	const theme = useTheme();
	return <div style={{ color: theme.colors.grey?.[400] }} {...props}></div>;
}

/** If you are using the simulated placeholder you should pass in a renderValue  */
/**
 * This LabeledSelect is just here to support @fox/data-grid is deprecated, use `{ LabledSelect } from 'foxd'`.
 * @deprecated
 */
export const DeprecatedLabeledSelect = (props: ISelectProps & { formControlStyle?: React.CSSProperties }) => {
	const id = useRef(nanoid());
	const { formControlStyle, ...selectProps } = props;
	if (props.placeholder && !props.renderValue) {
		throw new Error('placeholder should be used with a renderValue function but it is undefined');
	}
	// this is for the simulated placeholder
	const r = value =>
		value?.length ? props.renderValue?.(value) : <PlaceholderTextColorWrapper>{props.placeholder ?? ''}</PlaceholderTextColorWrapper>;
	return (
		<FormControl size={props.size} variant={props.variant ?? 'standard'} style={formControlStyle}>
			<InputLabel size={props.size === 'medium' ? 'normal' : 'small'} id={id.current} required={props.required}>
				{props.label}
			</InputLabel>
			<DepcrecatedSelect
				// note that the ordering of some of these props matter
				displayEmpty={!!props.placeholder} // this is for the simulated placeholder
				{...selectProps}
				labelId={id.current}
				required={props.required}
				renderValue={props.renderValue ? r : undefined}
			/>
		</FormControl>
	);
};

/**
 * ChipSelect is deprecated, use `{ Select } from '@fox/ui'`. Please see Storybook for help with Select and report any issues.
 * @deprecated
 */
export const DepcrecatedChipSelect = (
	props: ISelectProps & {
		chips: (IChipProps & IMenuItemProps & { dropDownOverride?: IMenuItemProps })[];
	}
) => {
	return (
		<>
			<DepcrecatedSelect
				{...props}
				displayEmpty={!!props.placeholder}
				renderValue={s => {
					const selected = Array.isArray(props.value) ? props.value : [props.value];

					if (props.placeholder && s.length === 0) {
						return <>{props.placeholder}</>;
					} else {
						return (
							<div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
								{selected.map((s, i) => {
									return (
										<div
											style={{
												cursor: 'pointer'
											}}
											key={s}
										>
											<Chip
												{...props.chips.find(f => f.value === s)}
												onDelete={
													props.readOnly
														? undefined
														: e => {
																if (props.onChange) {
																	const filtered = selected.filter(f => f !== s);
																	props.onChange({
																		...e,
																		target: {
																			...e.target,
																			value: props.multiple
																				? filtered
																				: filtered.length > 0
																					? filtered[0]
																					: null
																		}
																	});
																}
															}
												}
											/>
										</div>
									);
								})}
							</div>
						);
					}
				}}
			>
				{props.chips.map(c => {
					const s = c;
					if (s.dropDownOverride) {
						return <MenuItem {...s.dropDownOverride} key={s.value} />;
					}
					return (
						<MenuItem
							key={s.value}
							value={s.value}
							{...s}
							style={{
								cursor: 'pointer'
							}}
						>
							<Chip {...s} onDelete={undefined} />
						</MenuItem>
					);
				})}
				{props.children}
			</DepcrecatedSelect>
		</>
	);
};
